import { getUserInfo } from './auth';
import { getApiV1Url } from '../config/api';

export interface BoardCategory {
  projectCode?: string;
  boardCode?: string;
  code: string;
  name: string;
  status: string;
  sortOrder: string;
  postCount?: number;
  createdBy?: string;
  createdAt?: string;
  updatedBy?: string;
  updatedAt?: string;
}

export interface BoardCategoryCreateRequest {
  code: string;
  name: string;
  status: string;
  sortOrder: string;
}

export interface BoardCategoryUpdateRequest {
  code: string;
  name: string;
  status: string;
  sortOrder: string;
}

export interface GetBoardCategoryListParams {
  includePostCount?: boolean;
}

interface ApiErrorBody {
  timestamp?: number;
  errors?: {
    error?: Array<{ code: number; target: string; message: string }>;
  };
  message?: string;
  data?: unknown;
}

function getAuthHeaders(): Record<string, string> {
  const userInfo = getUserInfo();
  if (!userInfo) {
    throw new Error('사용자 정보가 없습니다. 로그인을 다시 시도해주세요.');
  }
  return {
    'Content-Type': 'application/json',
    'X-Account-Code': userInfo.accountCode,
    'X-User-Id': userInfo.userType === 'root' ? userInfo.accountCode : userInfo.userId,
  };
}

function categoriesBasePath(projectCode: string, boardCode: string): string {
  return `/projects/${projectCode}/boards/${encodeURIComponent(boardCode)}/categories`;
}

async function parseApiResponse<T>(response: Response): Promise<T> {
  const result: ApiErrorBody = await response.json().catch(() => ({}));

  if (result.errors?.error && result.errors.error.length > 0) {
    const errorMessage = result.errors.error.map((e) => e.message).join(', ');
    throw new Error(errorMessage);
  }

  if (!response.ok) {
    throw new Error(result.message || `HTTP error! status: ${response.status}`);
  }

  return result as T;
}

function normalizeCategory(raw: Record<string, unknown>): BoardCategory {
  return {
    projectCode: typeof raw.projectCode === 'string' ? raw.projectCode : undefined,
    boardCode: typeof raw.boardCode === 'string' ? raw.boardCode : undefined,
    code: String(raw.code ?? ''),
    name: String(raw.name ?? ''),
    status: String(raw.status ?? 'ACTIVE'),
    sortOrder: String(raw.sortOrder ?? ''),
    postCount:
      raw.postCount != null || raw.articleCount != null
        ? Number(raw.postCount ?? raw.articleCount ?? 0) || 0
        : undefined,
    createdBy: typeof raw.createdBy === 'string' ? raw.createdBy : undefined,
    createdAt: typeof raw.createdAt === 'string' ? raw.createdAt : undefined,
    updatedBy: typeof raw.updatedBy === 'string' ? raw.updatedBy : undefined,
    updatedAt: typeof raw.updatedAt === 'string' ? raw.updatedAt : undefined,
  };
}

function extractCategories(data: unknown): BoardCategory[] {
  if (Array.isArray(data)) {
    return data.map((item) => normalizeCategory(item as Record<string, unknown>));
  }
  if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>;
    const list = obj.categories ?? obj.items ?? obj.list;
    if (Array.isArray(list)) {
      return list.map((item) => normalizeCategory(item as Record<string, unknown>));
    }
  }
  return [];
}

export function sortBoardCategories(categories: BoardCategory[]): BoardCategory[] {
  return [...categories].sort((a, b) => {
    const ao = a.sortOrder || '';
    const bo = b.sortOrder || '';
    if (ao !== bo) return ao.localeCompare(bo);
    return a.code.localeCompare(b.code);
  });
}

/**
 * 게시판 카테고리 목록
 * GET /api/v1/projects/{projectCode}/boards/{boardCode}/categories
 */
export const getBoardCategoryList = async (
  projectCode: string,
  boardCode: string,
  params?: GetBoardCategoryListParams
): Promise<BoardCategory[]> => {
  const query = params?.includePostCount ? '?includePostCount=true' : '';
  const response = await fetch(getApiV1Url(`${categoriesBasePath(projectCode, boardCode)}${query}`), {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  const result = await parseApiResponse<ApiErrorBody>(response);
  return sortBoardCategories(extractCategories(result.data));
};

/**
 * 게시판 카테고리 생성
 * POST /api/v1/projects/{projectCode}/boards/{boardCode}/categories
 */
export const createBoardCategory = async (
  projectCode: string,
  boardCode: string,
  body: BoardCategoryCreateRequest
): Promise<BoardCategory> => {
  const response = await fetch(getApiV1Url(categoriesBasePath(projectCode, boardCode)), {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      code: body.code.trim(),
      name: body.name.trim(),
      status: body.status,
      sortOrder: body.sortOrder,
    }),
  });

  const result = await parseApiResponse<ApiErrorBody>(response);
  const raw = result.data;
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    return normalizeCategory(raw as Record<string, unknown>);
  }
  return normalizeCategory({
    code: body.code,
    name: body.name,
    status: body.status,
    sortOrder: body.sortOrder,
  });
};

/**
 * 게시판 카테고리 수정
 * POST /api/v1/projects/{projectCode}/boards/{boardCode}/categories/{categoryCode}
 */
export const updateBoardCategory = async (
  projectCode: string,
  boardCode: string,
  categoryCode: string,
  body: BoardCategoryUpdateRequest
): Promise<BoardCategory> => {
  const response = await fetch(
    getApiV1Url(`${categoriesBasePath(projectCode, boardCode)}/${encodeURIComponent(categoryCode)}`),
    {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        code: body.code.trim(),
        name: body.name.trim(),
        status: body.status,
        sortOrder: body.sortOrder,
      }),
    }
  );

  const result = await parseApiResponse<ApiErrorBody>(response);
  const raw = result.data;
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    return normalizeCategory(raw as Record<string, unknown>);
  }
  return normalizeCategory({
    code: body.code,
    name: body.name,
    status: body.status,
    sortOrder: body.sortOrder ?? '',
  });
};

/**
 * 게시판 카테고리 삭제
 * DELETE /api/v1/projects/{projectCode}/boards/{boardCode}/categories/{categoryCode}
 */
export const deleteBoardCategory = async (
  projectCode: string,
  boardCode: string,
  categoryCode: string
): Promise<void> => {
  const response = await fetch(
    getApiV1Url(`${categoriesBasePath(projectCode, boardCode)}/${encodeURIComponent(categoryCode)}`),
    {
      method: 'DELETE',
      headers: getAuthHeaders(),
    }
  );

  await parseApiResponse<ApiErrorBody>(response);
};
